import React, { useState, useEffect } from 'react';

/**
 * TeamAssignmentModal -- explicit 2v2 side assignment, with a confirm step.
 *
 * Replaces the old invisible click-order side assignment: for a 2v2 battle
 * the admin must explicitly place each of the 4 selected artists into
 * Side 1 or Side 2 and confirm before the assignment is accepted.
 *
 * Props:
 *   show          boolean
 *   artists       [{ id, display_name?, username? }]  -- exactly 4 artists
 *   initialSides  { "1": [id, id], "2": [id, id] } | null  -- prefill (edit flow)
 *   onConfirm     (sides: { "1": [id,id], "2": [id,id] }) => void
 *   onCancel      () => void
 */
const TeamAssignmentModal = ({ show, artists = [], initialSides, onConfirm, onCancel }) => {
  const label = (artist) => artist.display_name || artist.username;

  const defaultSides = () => {
    if (initialSides && initialSides['1']?.length === 2 && initialSides['2']?.length === 2) {
      return {
        1: initialSides['1'].map(String),
        2: initialSides['2'].map(String),
      };
    }
    const ids = artists.map((a) => a.id.toString());
    return { 1: ids.slice(0, 2), 2: ids.slice(2, 4) };
  };

  const [sides, setSides] = useState(defaultSides);

  // Re-derive whenever the modal is (re)opened for a possibly different
  // artist set (e.g. admin re-opens after changing the picker selection).
  useEffect(() => {
    if (show) setSides(defaultSides());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, artists.map((a) => a.id).join(','), JSON.stringify(initialSides)]);

  if (!show) return null;

  const moveTo = (artistId, targetSide) => {
    const id = artistId.toString();
    setSides((prev) => {
      const next = { 1: prev[1].filter((x) => x !== id), 2: prev[2].filter((x) => x !== id) };
      next[targetSide] = [ ...next[targetSide], id ];
      return next;
    });
  };

  const isValid = sides[1].length === 2 && sides[2].length === 2;

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({ 1: sides[1], 2: sides[2] });
  };

  const renderColumn = (sideNum) => {
    const ids = sides[sideNum];
    return (
      <div className="flex-1" data-testid={`side-${sideNum}-column`}>
        <h4 className="text-brand-yellow font-bold text-sm mb-2">
          Side {sideNum} ({ids.length}/2)
        </h4>
        <div className="bg-battle-dark rounded-lg p-2 min-h-[100px] space-y-1">
          {artists
            .filter((a) => ids.includes(a.id.toString()))
            .map((artist) => (
              <div
                key={artist.id}
                className="flex items-center justify-between bg-battle-gray rounded px-2 py-1"
                data-testid={`team-artist-${artist.id}`}
              >
                <span className="text-white text-sm">{label(artist)}</span>
                <button
                  type="button"
                  onClick={() => moveTo(artist.id, sideNum === 1 ? 2 : 1)}
                  className="text-xs text-brand-yellow hover:text-brand-yellow-light ml-2"
                  data-testid={`move-artist-${artist.id}-to-side-${sideNum === 1 ? 2 : 1}`}
                >
                  Move to Side {sideNum === 1 ? 2 : 1} &rarr;
                </button>
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4"
      data-testid="team-assignment-modal"
    >
      <div className="bg-battle-gray rounded-lg max-w-lg w-full p-4 sm:p-6 mx-4 sm:mx-auto">
        <h3 className="text-xl font-bold text-white mb-1">Assign 2v2 Teams</h3>
        <p className="text-gray-400 text-xs mb-4">
          Place each artist on Side 1 or Side 2, then confirm.
        </p>

        <div className="flex gap-4 mb-4">
          {renderColumn(1)}
          {renderColumn(2)}
        </div>

        {!isValid && (
          <p className="text-red-400 text-xs mb-4" data-testid="team-assignment-error">
            Each side needs exactly 2 artists.
          </p>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            data-testid="cancel-teams-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isValid}
            className={`btn-primary ${!isValid ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="confirm-teams-btn"
          >
            Confirm Teams
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamAssignmentModal;
